import React from "react";
import PropTypes from "prop-types";
import {
  SIGNOL_COLOUR_NAMES,
  SIGNOL_COLOUR_NAMES_WITH_SHADES,
} from "@signolteam/shared/colours";
import { useManagerContext } from "@src/context/managerContext";
import { useIsTabletWidth } from "@src/utils/mediaQuery";
import AppUtils from "@src/utils/appUtils";
import { getNumberFormatterForUnit } from "@src/components/utility/getNumberFormatted";
import Loader from "@src/components/atoms/Loader";
import Icon from "@src/components/atoms/Icon";
import CardContainer from "@src/components/cards/CardContainer";
import VerticalLayout from "@src/components/cards/VerticalLayout";
import { COMMON_CONTROLS } from "@src/stories/utils/common-controls";

const ManagersBehaviourChangeCard = ({
  behaviourName,
  behaviourAcronym,
  clickHandler,
  behaviourColour,
  attributeText,
  postSignolValue,
  postSignolStandardError,
  changeVsPreValue,
  changeVsPreStandardError,
  isLoading = false,
  unit = "%",
  preSignolText = "vs pre-Signol",
  significant,
}) => {
  const { profile } = useManagerContext();
  const isTabletWidth = useIsTabletWidth();
  const industry = profile.customer.industry;
  const formatFunction = getNumberFormatterForUnit(unit, industry);

  const change =
    Math.abs(changeVsPreValue) < 0.1
      ? "NEUTRAL"
      : changeVsPreValue > 0
        ? "POSITIVE"
        : "NEGATIVE";

  const handleClick =
    typeof clickHandler === "function"
      ? () => {
          if (behaviourAcronym && clickHandler) {
            clickHandler(behaviourAcronym);
          }
        }
      : undefined;

  let dataAvailable = true;
  if (postSignolValue === null || postSignolValue === undefined) {
    dataAvailable = false;
  }

  const dataAvailableChangeVsPre =
    typeof changeVsPreStandardError === "number" &&
    typeof changeVsPreValue === "number";

  const statePostSignol = isLoading
    ? "loading"
    : dataAvailable
      ? "loaded"
      : "missing";
  const stateVsPreSignol = isLoading
    ? "loading"
    : dataAvailable && dataAvailableChangeVsPre
      ? "loaded"
      : "missing";

  const measurementPrimary = formatFunction(
    postSignolValue,
    postSignolStandardError,
  );
  const measurementBaselineChange = formatFunction(
    changeVsPreValue,
    changeVsPreStandardError,
    {
      includePositiveSign: true,
    },
  );

  return (
    <CardContainer thickBorderColor={behaviourColour} onClick={handleClick}>
      <VerticalLayout layoutType={"flex-bottom"}>
        <div className="h-full justify-between flex flex-col px-7 py-6">
          {behaviourName?.length > 0 && (
            <span
              className={`text-${behaviourColour}-500 font-bold max-w-48`}
              style={{ minHeight: "48px" }}
            >
              {behaviourName}
            </span>
          )}
          <span className="text-neutral-500 text-sm">{attributeText}</span>
          {statePostSignol === "loading" && <Loader inline />}
          {statePostSignol === "missing" && (
            <span className="text-black text-md">No data available</span>
          )}
          {statePostSignol === "loaded" && (
            <span>
              <span className="text-black text-4xl">
                {measurementPrimary.formattedValue}
              </span>
              {postSignolStandardError !== null &&
                postSignolStandardError !== undefined && (
                  <span
                    className={`ml-2 ${
                      behaviourColour ? `text-${behaviourColour}-500 ` : ""
                    }`}
                  >
                    {`±${measurementPrimary.formattedUncertainty}`}
                  </span>
                )}
            </span>
          )}
        </div>
        <div
          className={`${
            behaviourColour ? `bg-${behaviourColour}-100` : ""
          } h-full flex items-center justify-center pl-4 pr-4 rounded-b-lg`}
        >
          {stateVsPreSignol === "loading" && <Loader inline />}
          {stateVsPreSignol === "missing" && (
            <div className="flex flex-row justify-center items-center gap-4 p-2">
              <span>
                <Icon
                  name={"brokenChains"}
                  backgroundColor="transparent"
                  inline
                />
              </span>
              <span>No pre-signol data available for comparison</span>
            </div>
          )}
          {stateVsPreSignol === "loaded" && (
            <>
              <div className="flex-1 flex flex-col py-6 pl-2 pr-1">
                <span className="flex items-center text-4xl">
                  <span className="mr-2">
                    {measurementBaselineChange.formattedValue}
                  </span>
                  {changeVsPreStandardError !== null &&
                    changeVsPreStandardError !== undefined && (
                      <span
                        className={`${
                          behaviourColour ? `text-${behaviourColour}-500 ` : ""
                        } text-sm self-end`}
                      >
                        {`±${measurementBaselineChange.formattedUncertainty}`}
                      </span>
                    )}
                </span>
                <div className="flex gap-2 items-center">
                  {change !== "NEUTRAL" && (
                    <div
                      className="shrink-0"
                      style={{
                        rotate: `${change === "POSITIVE" ? "0deg" : "180deg"}`,
                      }}
                    >
                      <Icon
                        name="triangle"
                        size="sm"
                        backgroundColor="transparent"
                        textColor={`${behaviourColour}-300`}
                        padding={false}
                        centered={false}
                      />
                    </div>
                  )}
                  <span className="text-sm">{preSignolText}</span>
                  {significant !== undefined && (
                    <Icon
                      name={
                        significant
                          ? "statisticalSignificance"
                          : "notStatisticallySignificant"
                      }
                      size="sm"
                      backgroundColor="transparent"
                      padding={false}
                      centered={false}
                    />
                  )}
                </div>
              </div>{" "}
            </>
          )}
        </div>
      </VerticalLayout>
    </CardContainer>
  );
};

export const managersBehaviourChangeCardControls = {
  behaviourName: {
    type: "string",
    description: "Behaviour name",
    defaultValue: "Behaviour Name",
  },
  behaviourAcronym: COMMON_CONTROLS.behaviour_acronym,
  clickHandler: {
    type: "function",
    description: "Click handler function",
    defaultValue: () => {},
  },
  behaviourColour: COMMON_CONTROLS.colour_name_shaded,
  attributeText: {
    type: "string",
    description: "Attribute text",
    defaultValue: "Average fuel saving:",
  },
  postSignolValue: {
    type: "number",
    description: "Post Signol value",
    defaultValue: 20,
  },
  postSignolStandardError: {
    type: "number",
    description: "Post Signol standard error",
    defaultValue: 5,
  },
  changeVsPreValue: {
    type: "number",
    description: "Success rate change value",
    defaultValue: 15,
  },
  changeVsPreStandardError: {
    type: "number",
    description: "Success rate change standard error",
    defaultValue: 4,
  },
  isLoading: COMMON_CONTROLS.isLoading,
  unit: {
    type: "string",
    description: "Unit of measurement",
    defaultValue: "%",
    controlType: "select",
    options: ["%", "kg", "t"],
  },
  className: COMMON_CONTROLS.className,
  preSignolText: {
    type: "string",
    description: "Pre-Signol text",
    defaultValue: "vs pre-Signol",
  },
  industry: COMMON_CONTROLS.industry,
};

ManagersBehaviourChangeCard.propTypes = AppUtils.getPropTypeFromControls(
  managersBehaviourChangeCardControls,
);

export default ManagersBehaviourChangeCard;
